import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "./Header";
import Footers from "./Footers";
import {
  FiArrowRight,
  FiSearch,
  FiClock,
  FiStar,
  FiCode,
  FiDatabase,
  FiBriefcase,
  FiPenTool,
  FiTrendingUp,
  FiCloud,
  FiShield,
  FiCpu,
  FiUsers,
  FiBookOpen,
  FiAward,
} from "react-icons/fi";
import {
  HiOutlineAcademicCap,
  HiOutlineOfficeBuilding,
  HiOutlineUserGroup,
} from "react-icons/hi";
import { MdOutlineWorkOutline } from "react-icons/md";
import { BsLaptop } from "react-icons/bs";
import ProfileImage from "./Profile.png";
import AxiosInstance from "./AxiosInstance";
import "./theme.css";
import "./Home.css";

const BASE_PATH = (process.env.REACT_APP_BASE_PATH || "/als").replace(/\/+$/, "");

const CATEGORIES = [
  { label: "Development", icon: FiCode, color: "#5b5fff" },
  { label: "Data Science", icon: FiDatabase, color: "#0ea5e9" },
  { label: "Business", icon: FiBriefcase, color: "#f59e0b" },
  { label: "Design", icon: FiPenTool, color: "#ec4899" },
  { label: "Marketing", icon: FiTrendingUp, color: "#10b981" },
  { label: "Cloud", icon: FiCloud, color: "#6366f1" },
  { label: "Security", icon: FiShield, color: "#ef4444" },
  { label: "AI / ML", icon: FiCpu, color: "#8b5cf6" },
];

const JOURNEYS = [
  {
    title: "Individual Trainers",
    desc: "Share your expertise and grow your teaching practice.",
    cta: "Become a Trainer",
    href: `${BASE_PATH}/trainers`,
    icon: HiOutlineAcademicCap,
  },
  {
    title: "Training Institutes",
    desc: "List programs and reach motivated learners nationwide.",
    cta: "List Your Institute",
    href: `${BASE_PATH}/institutions`,
    icon: HiOutlineOfficeBuilding,
  },
  {
    title: "Internships",
    desc: "Discover real-world roles that build career-ready skills.",
    cta: "Browse Internships",
    href: `${BASE_PATH}/internships`,
    icon: MdOutlineWorkOutline,
  },
  {
    title: "Companies",
    desc: "Hire emerging talent and grow your next generation team.",
    cta: "Browse Companies",
    href: `${BASE_PATH}/employers`,
    icon: HiOutlineUserGroup,
  },
  {
    title: "Students",
    desc: "Learn from experts and land opportunities that matter.",
    cta: "Start Learning",
    href: `${BASE_PATH}/courses`,
    icon: BsLaptop,
  },
];

const TESTIMONIALS = [
  {
    quote:
      "The courses were practical and the internship I found here led to my first full-time role.",
    name: "Ananya Reddy",
    role: "Data Analyst",
  },
  {
    quote:
      "As a trainer, Open Learnings helped me reach students I never could have found on my own.",
    name: "Rahul Mehta",
    role: "Cloud Instructor",
  },
  {
    quote:
      "We hired three interns through the platform — all were well-prepared and eager to learn.",
    name: "Priya Sharma",
    role: "HR Lead",
  },
];

const HERO_IMAGES = [
  "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=400&h=480&fit=crop",
  "https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=360&h=420&fit=crop",
  "https://images.unsplash.com/photo-1517486808906-6ca8b3f04846?w=340&h=400&fit=crop",
];

const getLogoUrl = (logo) => {
  if (!logo) return null;
  const path = String(logo).replace(/\\/g, "/");
  if (path.startsWith("http")) return path;
  return `${AxiosInstance.defaults.baseURL}/media/${path}`;
};

export default function Home() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [users, setUsers] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [partners, setPartners] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [testimonialIndex, setTestimonialIndex] = useState(0);

  useEffect(() => {
    AxiosInstance.get("companies/batches/")
      .then((response) => {
        const publishedCourses = response.data.filter(
          (course) => course.is_published === "Yes"
        );
        setCourses(publishedCourses);
      })
      .catch((error) => console.error("Error fetching courses:", error));
  }, []);

  useEffect(() => {
    AxiosInstance.get("companies/users")
      .then((response) => setUsers(response.data))
      .catch((error) => console.error("Error fetching users:", error));

    AxiosInstance.get("companies/trainers")
      .then((response) => setTrainers(response.data))
      .catch((error) => console.error("Error fetching trainers:", error));
  }, []);

  useEffect(() => {
    AxiosInstance.get("companies/companies")
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        setCompanies(list);
        const shuffled = [...list].sort(() => 0.5 - Math.random());
        setPartners(shuffled.slice(0, 6));
      })
      .catch((err) => console.error("Error fetching companies:", err));
  }, []);

  useEffect(() => {
    const id = setInterval(() => {
      setTestimonialIndex((i) => (i + 1) % TESTIMONIALS.length);
    }, 5000);
    return () => clearInterval(id);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    navigate(q ? `/courses?q=${encodeURIComponent(q)}` : `/courses`);
  };

  const stats = [
    { value: `${Math.max(courses.length, 50)}+`, label: "Courses", icon: FiBookOpen },
    { value: `${Math.max(trainers.length, 10)}+`, label: "Trainers", icon: FiUsers },
    { value: `${Math.max(companies.length, 2)}+`, label: "Institutes", icon: HiOutlineOfficeBuilding },
    { value: `${Math.max(users.length, 100)}+`, label: "Students", icon: FiAward },
  ];

  return (
    <div className="homepage als-public">
      <Header />

      {/* Hero */}
      <section className="als-hero">
        <div className="als-hero-glow als-hero-glow-1" />
        <div className="als-hero-glow als-hero-glow-2" />
        <div className="als-hero-inner">
          <div className="als-hero-copy">
            <h1>
              Learn. Grow. <span>Achieve.</span>
            </h1>
            <p>
              Your trusted partner in professional training, internships and
              lifelong learning.
            </p>
            <div className="als-hero-ctas">
              <Link to="/courses" className="als-btn als-btn-primary">
                Explore Courses
              </Link>
              <Link to="/internships" className="als-btn als-btn-outline">
                Find Internships
              </Link>
            </div>
          </div>
          <div className="als-hero-visual" aria-hidden="true">
            <div className="als-hero-frame als-hero-frame-1">
              <img src={HERO_IMAGES[0]} alt="" />
            </div>
            <div className="als-hero-frame als-hero-frame-2">
              <img src={HERO_IMAGES[1]} alt="" />
            </div>
            <div className="als-hero-frame als-hero-frame-3">
              <img src={HERO_IMAGES[2]} alt="" />
            </div>
          </div>
        </div>
      </section>

      <div className="als-hero-search-wrap">
        <form className="als-hero-search" onSubmit={handleSearch}>
          <div className="als-hero-search-field">
            <FiSearch />
            <input
              type="text"
              placeholder="What do you want to learn?"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <select defaultValue="" aria-label="Category">
            <option value="">All Categories</option>
            {CATEGORIES.map((c) => (
              <option key={c.label} value={c.label}>
                {c.label}
              </option>
            ))}
          </select>
          <select defaultValue="" aria-label="Level">
            <option value="">All Levels</option>
            <option value="Beginner">Beginner</option>
            <option value="Intermediate">Intermediate</option>
            <option value="Advanced">Advanced</option>
          </select>
          <button type="submit" className="als-btn als-btn-primary">
            Search
          </button>
        </form>
      </div>

      {/* Categories */}
      <section className="als-section als-categories">
        <div className="als-section-inner">
          <div className="als-category-grid">
            {CATEGORIES.map((cat) => {
              const Icon = cat.icon;
              return (
                <Link
                  key={cat.label}
                  to="/courses"
                  className="als-category-item"
                >
                  <span
                    className="als-category-icon"
                    style={{ background: `${cat.color}18`, color: cat.color }}
                  >
                    <Icon size={22} />
                  </span>
                  <span>{cat.label}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Journeys */}
      <section className="als-section als-journeys">
        <div className="als-section-inner">
          <h2 className="als-section-title">We empower every learning journey.</h2>
          <div className="als-journey-grid">
            {JOURNEYS.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.title} className="als-journey-card">
                  <span className="als-journey-icon">
                    <Icon size={28} />
                  </span>
                  <h3>{item.title}</h3>
                  <p>{item.desc}</p>
                  <a href={item.href} className="als-link">
                    {item.cta} <FiArrowRight />
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured courses */}
      <section className="als-section als-featured">
        <div className="als-section-inner">
          <div className="als-section-head">
            <h2 className="als-section-title">Featured Courses</h2>
            <Link to="/courses" className="als-link">
              View All Courses <FiArrowRight />
            </Link>
          </div>
          <div className="als-course-grid">
            {courses.slice(0, 4).map((course, index) => {
              const name =
                course.course_material?.course?.course_name || "Course";
              const logo = course.course_material?.course?.course_logo;
              const badges = ["Bestseller", "Top Rated", "New", "Popular"];
              return (
                <Link
                  to={`/courses/${course.batch_id}`}
                  key={course.batch_id || index}
                  className="als-course-card"
                >
                  <div className="als-course-thumb">
                    {logo ? (
                      <img src={logo} alt={name} />
                    ) : (
                      <div className="als-course-thumb-fallback">{name[0]}</div>
                    )}
                    <span className="als-course-badge">{badges[index % badges.length]}</span>
                  </div>
                  <div className="als-course-body">
                    <h3>{name}</h3>
                    <p className="als-course-provider">
                      {course.batch_name || "Open Learnings"}
                    </p>
                    <div className="als-course-rating">
                      <FiStar />
                      <span>4.{8 - (index % 3)}</span>
                    </div>
                    <div className="als-course-meta">
                      <span>{course.mode || "Beginner"}</span>
                      <span>
                        <FiClock /> {course.duration || "4 Weeks"}
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
            {courses.length === 0 &&
              [1, 2, 3, 4].map((n) => (
                <div key={n} className="als-course-card als-course-placeholder">
                  <div className="als-course-thumb" />
                  <div className="als-course-body">
                    <h3>Coming soon</h3>
                    <p className="als-course-provider">New courses on the way</p>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </section>

      {/* Partners */}
      <section className="als-section als-partners">
        <div className="als-section-inner">
          <div className="als-partners-head">
            <h2 className="als-section-title">
              Learn from the best. Grow with the best.
            </h2>
            <p className="als-partners-sub">
              Top trainers. Leading institutes. Trusted by thousands.
            </p>
          </div>
          <div className="als-partners-logos">
            {partners.map((partner) => {
              const name = partner.company_name || "Partner";
              const logoUrl = getLogoUrl(partner.company_logo);
              return (
                <div
                  key={partner.company_id || name}
                  className="als-partner-card"
                  title={name}
                >
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt={name}
                      className="als-partner-logo"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                        const fallback = e.currentTarget.nextElementSibling;
                        if (fallback) fallback.hidden = false;
                      }}
                    />
                  ) : null}
                  <span
                    className="als-partner-mark"
                    hidden={Boolean(logoUrl)}
                  >
                    {name}
                  </span>
                </div>
              );
            })}
            {partners.length === 0 &&
              [1, 2, 3, 4, 5, 6].map((n) => (
                <div key={n} className="als-partner-card als-partner-placeholder">
                  <span className="als-partner-mark">Partner</span>
                </div>
              ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="als-stats">
        <div className="als-section-inner als-stats-grid">
          {stats.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.label} className="als-stat-item">
                <span className="als-stat-icon">
                  <Icon size={20} />
                </span>
                <div>
                  <strong>{s.value}</strong>
                  <span>{s.label}</span>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Testimonials */}
      <section className="als-section als-testimonials">
        <div className="als-section-inner">
          <h2 className="als-section-title">What our learners say.</h2>
          <div className="als-testimonial-grid">
            {TESTIMONIALS.map((t, i) => (
              <article
                key={t.name}
                className={`als-testimonial-card ${
                  i === testimonialIndex ? "active" : ""
                }`}
              >
                <p>“{t.quote}”</p>
                <div className="als-testimonial-author">
                  <img src={ProfileImage} alt="" />
                  <div>
                    <strong>{t.name}</strong>
                    <span>{t.role}</span>
                  </div>
                </div>
              </article>
            ))}
          </div>
          <div className="als-testimonial-dots">
            {TESTIMONIALS.map((_, i) => (
              <button
                key={i}
                type="button"
                className={i === testimonialIndex ? "active" : ""}
                onClick={() => setTestimonialIndex(i)}
                aria-label={`Show testimonial ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="als-section als-cta-section">
        <div className="als-section-inner">
          <div className="als-cta-banner">
            <div className="als-cta-copy">
              <h2>Ready to start your journey?</h2>
              <p>
                Join thousands of learners building skills and landing
                opportunities with Open Learnings.
              </p>
              <Link to="/trainee-signup" className="als-btn als-btn-primary">
                Get Started for Free
              </Link>
            </div>
            <div className="als-cta-visual">
              <img
                src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=520&h=420&fit=crop"
                alt="Student ready to learn"
              />
            </div>
          </div>
        </div>
      </section>

      <Footers />
    </div>
  );
}
